import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING = { role: "assistant", content: "Hi, I'm Ash — the 247 Aftermath assistant. Water, fire, mold or storm damage? I can answer questions or book a free on-site assessment. How can I help?" };

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/chat`, { messages: next.filter((m) => m.role !== "system") });
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: "Sorry, I had trouble there. For urgent help call 708-603-7046 (24/7)." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle"
        className="fixed bottom-6 right-6 z-[70] h-16 w-16 rounded-full bg-gold text-black shadow-2xl flex items-center justify-center hover:bg-gold-light transition-colors"
        style={{ boxShadow: "0 10px 40px rgba(212,175,55,0.4)" }}
        aria-label="Chat with us"
      >
        <AnimatePresence mode="wait">
          {open ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}><X className="h-7 w-7" /></motion.span>
                : <motion.span key="c" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}><MessageCircle className="h-7 w-7" /></motion.span>}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="fixed bottom-24 right-6 z-[70] w-[90vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col border border-gold/30 bg-[#0a0a0a] overflow-hidden"
            data-testid="chat-panel"
          >
            <div className="px-5 py-4 border-b border-white/10 bg-[#0d0d0d]">
              <p className="font-display text-lg text-[#F5F5F0]">247 Aftermath <span className="text-gold">Assistant</span></p>
              <p className="text-[11px] text-[#A3A3A3] font-mono uppercase tracking-widest">Online · 24/7 Emergency: 708-603-7046</p>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-gold text-black" : "bg-[#151515] text-[#F5F5F0] border border-white/10"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start"><div className="bg-[#151515] border border-white/10 px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-gold" /></div></div>
              )}
            </div>

            <div className="p-3 border-t border-white/10 bg-[#0d0d0d] flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your message..."
                data-testid="chat-input"
                className="flex-1 bg-[#151515] border border-white/10 px-3 py-2.5 text-sm text-[#F5F5F0] focus:outline-none focus:border-gold"
              />
              <button onClick={send} disabled={loading} data-testid="chat-send" className="h-10 w-10 shrink-0 bg-gold text-black flex items-center justify-center hover:bg-gold-light transition-colors disabled:opacity-60">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
